import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, Smartphone, Copy, Check, Send, Loader2, Sparkles, Inbox, Radio, ExternalLink } from "lucide-react";
import { FaxIntake } from "./FaxIntake";
import { toast } from "sonner";
import { forwardDocument, inboundStatus } from "../lib/api";
import { InboxLog } from "./InboxLog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";


const SAMPLE_EMAIL =
  "Subject: Your Geico Auto Policy Renewal\n\nDear policyholder, your auto insurance policy #GEC-4471902 has renewed. Premium: $842.00 for 6 months. Coverage: Liability, Collision, Comprehensive. Deductible: $500. Effective July 1, 2026 to Dec 31, 2026. Vehicle: 2022 Toyota RAV4.";
const SAMPLE_SMS =
  "Starbucks Reserve Receipt — Order #A2291. Total $18.45 paid Apple Pay •••• 4417 on June 30 2026. Items: 2x Cold Brew, 1x Almond Croissant. Store #1120 Seattle WA.";

const formatPhone = (raw) => {
  const m = String(raw || "").match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : raw;
};

const CopyRow = ({ icon: Icon, label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 min-w-0 overflow-hidden">
      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">
          {label}
        </div>
        <div className="font-mono text-sm truncate">{value}</div>
      </div>
      <Button
        data-testid={testid}
        size="icon"
        variant="ghost"
        onClick={copy}
        className="ml-auto shrink-0"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
};

const LiveInbox = ({ status }) => {
  if (!status) return null;
  if (status.configured && status.address) {
    return (
      <div data-testid="live-inbox" className="space-y-2 min-w-0 overflow-hidden">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-emerald-600">
          <Radio className="h-3.5 w-3.5" /> Live inbox · {status.received_count} received
        </div>
        <CopyRow icon={Mail} label="Forward emails to" value={status.address} testid="copy-email-btn" />
        {status.household_slug && (
          <p className="text-[11px] text-muted-foreground" data-testid="household-inbox-note">
            This address is unique to your household — anything sent here files straight into this vault, never into another family's.
          </p>
        )}
      </div>
    );
  }
  return (
    <div data-testid="inbox-setup" className="rounded-xl border border-dashed border-border bg-muted/30 p-4 space-y-2.5 min-w-0 overflow-hidden">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-muted-foreground">
        <Inbox className="h-3.5 w-3.5" /> Get a real forwarding address (2 min, free)
      </div>
      <ol className="text-xs text-muted-foreground space-y-1 list-decimal pl-4">
        <li>
          In Resend open <span className="font-medium text-foreground">Emails → Receiving</span> and copy your <span className="font-mono">@….resend.app</span> address.
        </li>
        <li>
          Add a webhook for <span className="font-mono">email.received</span> pointing to the URL below.
        </li>
        <li>Paste the signing secret and address into the vault settings (ask your builder) — done.</li>
      </ol>
      <CopyRow icon={ExternalLink} label="Webhook URL" value={`${window.location.origin}/api/inbound/resend`} testid="copy-webhook-btn" />
    </div>
  );
};

export const ForwardingHubModal = ({ open, onClose, onForwarded }) => {
  const { data: status } = useQuery({ queryKey: ["inbound-status"], queryFn: inboundStatus, enabled: open });
  const [emailText, setEmailText] = useState(SAMPLE_EMAIL);
  const [smsText, setSmsText] = useState(SAMPLE_SMS);
  const [busy, setBusy] = useState(false);

  const simulate = async (source, content) => {
    if (!content.trim()) {
      toast.error("Add some content to forward first");
      return;
    }
    setBusy(true);
    try {
      const doc = await forwardDocument({ content, source });
      toast.success(`Filed from ${source === "sms" ? "text" : "email"}`, {
        description: `${doc.title} → ${doc.category}`,
      });
      onForwarded?.(doc);
      onClose();
    } catch (e) {
      toast.error("Could not process the forwarded content");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid="forwarding-modal" className="sm:max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Forward via Email, Text & Fax</DialogTitle>
          <DialogDescription>
            Emails, texts and faxes sent to your vault addresses file themselves — attachments and all. The sandbox below
            lets you try the same AI pipeline instantly.
          </DialogDescription>
        </DialogHeader>

        <LiveInbox status={status} />
        <FaxIntake />
        {status?.received_count > 0 && (
          <div data-testid="forwarding-inbox-log" className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-muted-foreground"><Inbox className="h-3.5 w-3.5" /> Last {Math.min(status.received_count, 20)} emails in</div>
            <div className="max-h-52 overflow-y-auto pr-1"><InboxLog limit={20} showCounts={false} /></div>
          </div>
        )}

        <Tabs defaultValue="email" className="mt-2">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="email" className="gap-2">
              <Mail className="h-4 w-4" /> Email sandbox
            </TabsTrigger>
            <TabsTrigger value="sms" className="gap-2">
              <Smartphone className="h-4 w-4" /> Text sandbox
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="mt-3 space-y-3">
            <Textarea
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              rows={5}
              className="font-mono text-xs resize-none"
              placeholder="Paste a forwarded email…"
            />
            <Button
              data-testid="simulate-forward-email-btn"
              disabled={busy}
              onClick={() => simulate("email", emailText)}
              className="w-full gap-2 bg-primary hover:bg-primary/90"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Simulate forwarded email
            </Button>
          </TabsContent>

          <TabsContent value="sms" className="mt-3 space-y-3">
            {status?.sms_number && (
              <div className="space-y-1.5">
                <CopyRow icon={Smartphone} label="Text documents to" value={formatPhone(status.sms_number)} testid="copy-sms-number-btn" />
                <p className="text-[11px] text-muted-foreground" data-testid="sms-number-note">
                  Text or MMS a receipt, bill or photo to this number and it files itself into your vault. Try the sandbox below first.
                </p>
              </div>
            )}
            <Textarea
              value={smsText}
              onChange={(e) => setSmsText(e.target.value)}
              rows={5}
              className="font-mono text-xs resize-none"
              placeholder="Paste a texted receipt…"
            />
            <Button
              data-testid="simulate-forward-sms-btn"
              disabled={busy}
              onClick={() => simulate("sms", smsText)}
              className="w-full gap-2 bg-primary hover:bg-primary/90"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Simulate texted receipt
            </Button>
          </TabsContent>
        </Tabs>

        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          The sandbox runs real AI extraction — no email server setup required.
        </p>
      </DialogContent>
    </Dialog>
  );
};
